/**
 * AI Provider Settings Page
 *
 * Configure AI providers and task routing for AI Sales Assistant.
 */

import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import PasswordInput from '../../components/ui/PasswordInput';
import apiService, { extractApiErrorMessage } from '../../services/api';

interface AIProvider {
  provider: string;
  status: string;
  available: boolean;
  response?: string;
  error?: string;
}

interface AIProviderSettings {
  default_provider: string;
  failover_enabled: boolean;
  task_routing: Record<string, string>;
  available_providers: AIProvider[];
  configured_models?: Record<string, string>;
}

interface LiveModel {
  name: string;
  display_name: string;
  owned_by?: string;
  context_window?: string;
  description?: string;
}

interface ProviderConfig {
  value: string;
  label: string;
  color: string;
  setupInstructions: string;
  apiKeyUrl: string;
}

/**
 * Lightweight Markdown → HTML converter for AI pricing guidance responses.
 * Handles: headers, bold, italic, tables, lists, code blocks, line breaks.
 */
function formatMarkdown(md: string): string {
  if (!md) return '';
  let html = md
    // Code blocks
    .replace(
      /```(\w*)\n([\s\S]*?)```/g,
      '<pre class="bg-gray-100 rounded p-3 text-xs overflow-x-auto my-2"><code>$2</code></pre>'
    )
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
    // Headers
    .replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-sm mt-3 mb-1">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 class="font-semibold text-base mt-4 mb-1">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-bold text-base mt-4 mb-2">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="font-bold text-lg mt-4 mb-2">$1</h1>')
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="my-3 border-gray-300"/>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    // Ordered lists
    .replace(/^\d+\.\s(.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
    // Tables
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter((c) => c.trim() !== '');
      const isHeader = cells.every((c) => /^[\s-:]+$/.test(c));
      if (isHeader) return '<!--sep-->';
      const tag = 'td';
      const cellsHtml = cells
        .map(
          (c) => `<${tag} class="px-3 py-1.5 border border-gray-200 text-xs">${c.trim()}</${tag}>`
        )
        .join('');
      return `<tr>${cellsHtml}</tr>`;
    })
    // Wrap table rows
    .replace(
      /((<tr>.*<\/tr>\n?)+)/g,
      '<div class="overflow-x-auto my-2"><table class="min-w-full border-collapse border border-gray-200 text-sm">$1</table></div>'
    )
    // Clean up separator rows
    .replace(/<!--sep-->\n?/g, '')
    // Line breaks (double newline → paragraph break)
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  return html;
}

const AIProviderSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AIProviderSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderConfig | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [liveModels, setLiveModels] = useState<LiveModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Pricing guidance state
  const [pricingGuidance, setPricingGuidance] = useState<string>('');
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showPricingPanel, setShowPricingPanel] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState<string>('');
  const [modalTab, setModalTab] = useState<'config' | 'models' | 'pricing'>('config');

  // Form state
  const [defaultProvider, setDefaultProvider] = useState('openai');
  const [failoverEnabled, setFailoverEnabled] = useState(true);
  const [taskRouting, setTaskRouting] = useState<Record<string, string>>({});

  const providerConfigs: ProviderConfig[] = [
    {
      value: 'openai',
      label: 'OpenAI',
      color: 'green',
      apiKeyUrl: 'https://platform.openai.com/api-keys',
      setupInstructions:
        'Get your API key from OpenAI Platform. Go to API Keys section and create a new secret key.',
    },
    {
      value: 'anthropic',
      label: 'Anthropic (Claude)',
      color: 'orange',
      apiKeyUrl: 'https://console.anthropic.com/settings/keys',
      setupInstructions:
        'Create an API key from Anthropic Console. Navigate to Settings > API Keys.',
    },
    {
      value: 'moonshot',
      label: 'Moonshot AI',
      color: 'purple',
      apiKeyUrl: 'https://platform.moonshot.cn/console/api-keys',
      setupInstructions: 'Sign up for Moonshot AI and generate an API key from the console.',
    },
    {
      value: 'xai',
      label: 'xAI (Grok)',
      color: 'blue',
      apiKeyUrl: 'https://console.x.ai/api-keys',
      setupInstructions: 'Access xAI console and create a new API key.',
    },
  ];

  const tasks = [
    { value: 'description', label: 'Vehicle Descriptions' },
    { value: 'title', label: 'Listing Titles' },
    { value: 'deal_suggestion', label: 'Deal Suggestions' },
    { value: 'lead_scoring', label: 'Lead Scoring' },
    { value: 'online_marketing', label: 'Online Marketing' },
    { value: 'stocking_guide', label: 'Stocking Guide' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiService.get<AIProviderSettings>('/settings/ai-providers');
      setSettings(data);
      setDefaultProvider(data.default_provider);
      setFailoverEnabled(data.failover_enabled);
      setTaskRouting(data.task_routing);
    } catch (error) {
      console.error('Failed to fetch AI provider settings:', error);
      toast.error(extractApiErrorMessage(error, 'Failed to load AI provider settings'));
    } finally {
      setLoading(false);
    }
  };

  const testProvider = async (provider: string) => {
    setTesting(provider);
    try {
      const result = await apiService.post<AIProvider>(
        `/settings/ai-providers/test/${provider}`,
        {}
      );

      if (result.available) {
        toast.success(`${provider.toUpperCase()} connection successful!`);
      } else {
        toast.error(`${provider.toUpperCase()} connection failed: ${result.error}`);
      }

      // Refresh settings to update status
      fetchSettings();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to test ${provider}: ${message}`);
    } finally {
      setTesting(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.post('/settings/ai-providers', {
        default_provider: defaultProvider,
        failover_enabled: failoverEnabled,
        task_routing: taskRouting,
      });

      toast.success('AI provider settings saved successfully!');
      await fetchSettings();
    } catch (error: unknown) {
      console.error('Save error:', error);
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTaskRoutingChange = (task: string, provider: string) => {
    setTaskRouting((prev) => ({
      ...prev,
      [task]: provider,
    }));
  };

  const handleSaveProviderConfig = async () => {
    if (!selectedProvider) return;

    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSaving(true);
    try {
      // Save API key to backend (this would need a new endpoint)
      await apiService.post('/settings/ai-providers/configure', {
        provider: selectedProvider.value,
        api_key: apiKey,
        model: selectedModel,
      });

      toast.success(`${selectedProvider.label} configured successfully!`);
      await fetchSettings();
      setApiKey(''); // Clear the key from state for security
    } catch (error: unknown) {
      console.error('Config save error:', error);
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getProviderStatus = (providerName: string): AIProvider | undefined => {
    return settings?.available_providers.find((p) => p.provider === providerName);
  };

  const fetchProviderModels = async (providerValue: string) => {
    setModelsLoading(true);
    setLiveModels([]);
    try {
      const models = await apiService.get<LiveModel[]>(
        `/settings/ai-providers/${providerValue}/models`
      );
      setLiveModels(models);
      // Pre-select tenant's currently configured model, or first in list
      const currentModel = settings?.configured_models?.[providerValue];
      if (currentModel && models.some((m) => m.name === currentModel)) {
        setSelectedModel(currentModel);
      } else if (models.length > 0) {
        setSelectedModel(models[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
      toast.error('Failed to load models from provider');
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchPricingGuidance = async () => {
    if (!selectedProvider) return;
    setPricingLoading(true);
    setPricingGuidance('');
    setShowPricingPanel(true);
    try {
      const payload: Record<string, unknown> = {};
      if (selectedModel) payload.selected_model = selectedModel;
      if (monthlyBudget && parseFloat(monthlyBudget) > 0) {
        payload.monthly_budget = parseFloat(monthlyBudget);
      }
      const result = await apiService.post<{
        guidance: string;
        provider: string;
        model_used: string;
      }>(`/settings/ai-providers/${selectedProvider.value}/pricing-guidance`, payload);
      setPricingGuidance(result.guidance);
    } catch (error: unknown) {
      console.error('Failed to fetch pricing guidance:', error);
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      setPricingGuidance(
        `**Error:** ${detail || 'Failed to get pricing guidance. Make sure the provider is configured and reachable.'}`
      );
    } finally {
      setPricingLoading(false);
    }
  };

  const openSetupModal = (providerValue: string) => {
    const provider = providerConfigs.find((p) => p.value === providerValue);
    if (provider) {
      setSelectedProvider(provider);
      setSelectedModel('');
      setApiKey('');
      setShowSetupModal(true);
      fetchProviderModels(providerValue);
    }
  };

  const closeSetupModal = () => {
    setShowSetupModal(false);
    setSelectedProvider(null);
    setSelectedModel('');
    setApiKey('');
    setLiveModels([]);
    setPricingGuidance('');
    setShowPricingPanel(false);
    setPricingLoading(false);
    setMonthlyBudget('');
    setModalTab('config');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Provider Settings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure AI providers and routing for AI Sales Assistant features
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center">
          {saving ? (
            <>
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Cog6ToothIcon className="h-5 w-5 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* Provider Status Cards */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CpuChipIcon className="h-5 w-5 mr-2 text-blue-600" />
            Available Providers
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
          {providerConfigs.map((provider) => {
            const status = getProviderStatus(provider.value);
            const isAvailable = status?.available || false;
            const configuredModel = settings?.configured_models?.[provider.value];

            return (
              <div
                key={provider.value}
                onClick={() => openSetupModal(provider.value)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  isAvailable
                    ? 'border-green-200 bg-green-50 hover:border-green-300'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    {isAvailable ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                    ) : (
                      <XCircleIcon className="h-6 w-6 text-gray-400 mr-2" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{provider.label}</h3>
                      <p className="text-xs text-gray-500">
                        {isAvailable ? 'Connected' : 'Click to configure'}
                      </p>
                      {configuredModel && (
                        <p className="text-xs text-blue-600 mt-0.5">Model: {configuredModel}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      testProvider(provider.value);
                    }}
                    disabled={testing === provider.value}
                    className="btn-secondary text-xs px-3 py-1.5"
                  >
                    {testing === provider.value ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      'Test'
                    )}
                  </button>
                </div>
                {status?.error && <p className="text-xs text-red-600 mt-2">{status.error}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Default Provider Selection */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Default Provider</h2>
          <p className="text-sm text-gray-600 mt-1">
            Primary AI provider for all tasks (unless overridden below)
          </p>
        </div>
        <div className="p-6">
          <select
            value={defaultProvider}
            onChange={(e) => setDefaultProvider(e.target.value)}
            className="block w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {providerConfigs.map((provider) => (
              <option key={provider.value} value={provider.value}>
                {provider.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Task-Specific Routing */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Task-Specific Routing</h2>
          <p className="text-sm text-gray-600 mt-1">
            Override default provider for specific AI tasks
          </p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {tasks.map((task) => (
              <div key={task.value} className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">{task.label}</label>
                </div>
                <select
                  value={taskRouting[task.value] || defaultProvider}
                  onChange={(e) => handleTaskRoutingChange(task.value, e.target.value)}
                  className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="">Use Default ({defaultProvider})</option>
                  {providerConfigs.map((provider) => (
                    <option key={provider.value} value={provider.value}>
                      {provider.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Failover Settings */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Failover Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automatically try alternate providers if primary fails
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="failover"
              checked={failoverEnabled}
              onChange={(e) => setFailoverEnabled(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="failover" className="ml-2 block text-sm text-gray-900">
              Enable automatic failover to alternate providers
            </label>
          </div>
          {failoverEnabled && (
            <p className="text-xs text-gray-500 mt-2">
              If a provider fails, the system will automatically try: OpenAI → Moonshot → xAI →
              Anthropic → Template Fallbacks
            </p>
          )}
        </div>
      </div>

      {/* Setup Modal */}
      {showSetupModal && selectedProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-500/75" onClick={closeSetupModal} />

          <div
            className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl flex flex-col"
            style={{ maxHeight: 'calc(100vh - 3rem)' }}
          >
            {/* Compact Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <CpuChipIcon className={`h-6 w-6 text-${selectedProvider.color}-600`} />
                <h3 className="text-lg font-bold text-gray-900">{selectedProvider.label}</h3>
                {getProviderStatus(selectedProvider.value)?.available ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                    <CheckCircleIcon className="h-3.5 w-3.5" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    <XCircleIcon className="h-3.5 w-3.5" /> Not configured
                  </span>
                )}
              </div>
              <button onClick={closeSetupModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-5 flex-shrink-0">
              {[
                { key: 'config' as const, label: 'Configuration', icon: Cog6ToothIcon },
                {
                  key: 'models' as const,
                  label: `Models${liveModels.length ? ` (${liveModels.length})` : ''}`,
                  icon: CpuChipIcon,
                },
                { key: 'pricing' as const, label: 'Pricing', icon: BanknotesIcon },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setModalTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    modalTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Scrollable Tab Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* === CONFIG TAB === */}
              {modalTab === 'config' && (
                <div className="space-y-4">
                  {/* Instructions (compact) */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">{selectedProvider.setupInstructions}</p>
                    <a
                      href={selectedProvider.apiKeyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Get API Key →
                    </a>
                  </div>

                  {/* API Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                    <PasswordInput
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={`Enter your ${selectedProvider.label} API key`}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Stored securely. Only used for AI requests.
                    </p>
                  </div>

                  {/* Model dropdown */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Model</label>
                      <button
                        onClick={() => fetchProviderModels(selectedProvider.value)}
                        disabled={modelsLoading}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <ArrowPathIcon
                          className={`h-3 w-3 mr-1 ${modelsLoading ? 'animate-spin' : ''}`}
                        />
                        Refresh
                      </button>
                    </div>
                    {modelsLoading ? (
                      <div className="flex items-center gap-2 py-1.5 text-sm text-gray-500">
                        <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-600" /> Loading...
                      </div>
                    ) : liveModels.length === 0 ? (
                      <p className="text-sm text-gray-400 py-1">No models loaded yet.</p>
                    ) : (
                      <select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      >
                        {liveModels.map((m) => (
                          <option key={m.name} value={m.name}>
                            {m.display_name || m.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              )}

              {/* === MODELS TAB === */}
              {modalTab === 'models' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Available Models</span>
                      {liveModels.length > 0 && (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          Live
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => fetchProviderModels(selectedProvider.value)}
                      disabled={modelsLoading}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <ArrowPathIcon
                        className={`h-3 w-3 mr-1 ${modelsLoading ? 'animate-spin' : ''}`}
                      />
                      Refresh
                    </button>
                  </div>
                  {modelsLoading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                      <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-600" /> Loading
                      models from {selectedProvider.label}...
                    </div>
                  ) : liveModels.length === 0 ? (
                    <p className="text-sm text-gray-400 py-10 text-center">
                      No models available. Configure an API key first.
                    </p>
                  ) : (
                    <div className="rounded-md border border-gray-200 overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Model
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Info
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-20">
                                Select
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {liveModels.map((model) => (
                              <tr
                                key={model.name}
                                className={`cursor-pointer transition-colors ${selectedModel === model.name ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                onClick={() => setSelectedModel(model.name)}
                              >
                                <td className="px-3 py-2">
                                  <span className="font-medium text-gray-900 text-xs">
                                    {model.display_name || model.name}
                                  </span>
                                  {model.owned_by && (
                                    <span className="text-xs text-gray-400 ml-1">
                                      · {model.owned_by}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">
                                  {model.description || model.context_window || '—'}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {selectedModel === model.name ? (
                                    <CheckCircleIcon className="h-4 w-4 text-blue-600 inline" />
                                  ) : (
                                    <span className="text-xs text-blue-500">Use</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {liveModels.length > 0 && (
                    <p className="text-xs text-gray-400 mt-2">
                      Click a row to select. Fetched live from {selectedProvider.label} API.
                    </p>
                  )}
                </div>
              )}

              {/* === PRICING TAB === */}
              {modalTab === 'pricing' && (
                <div>
                  <div className="flex items-end gap-3 mb-3">
                    <div className="w-40">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Budget / mo (optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="5"
                          value={monthlyBudget}
                          onChange={(e) => setMonthlyBudget(e.target.value)}
                          placeholder="50"
                          className="block w-full pl-6 rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      onClick={fetchPricingGuidance}
                      disabled={pricingLoading}
                      className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {pricingLoading ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                          Asking...
                        </>
                      ) : (
                        <>
                          <BanknotesIcon className="h-4 w-4 mr-1.5" />
                          Get Pricing Guidance
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    Asks {selectedProvider.label} directly about model pricing, usage examples &amp;
                    budget tips.
                  </p>

                  {showPricingPanel && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50/40 overflow-hidden">
                      <div className="px-3 py-1.5 bg-emerald-100/60 border-b border-emerald-200 flex items-center justify-between">
                        <span className="text-xs font-medium text-emerald-800">
                          {pricingLoading
                            ? 'Generating...'
                            : `Response from ${selectedProvider.label}`}
                        </span>
                        {!pricingLoading && pricingGuidance && (
                          <button
                            onClick={() => {
                              setShowPricingPanel(false);
                              setPricingGuidance('');
                            }}
                            className="text-emerald-600 hover:text-emerald-800"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="p-3 max-h-64 overflow-y-auto">
                        {pricingLoading ? (
                          <div className="flex items-center justify-center gap-2 py-6">
                            <ArrowPathIcon className="h-4 w-4 animate-spin text-emerald-600" />
                            <span className="text-sm text-gray-500">
                              Querying {selectedProvider.label}...
                            </span>
                          </div>
                        ) : (
                          <div
                            className="prose prose-sm prose-emerald max-w-none text-gray-800 text-xs leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: formatMarkdown(pricingGuidance) }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Compact Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
              <button onClick={closeSetupModal} className="btn-secondary text-sm px-3 py-1.5">
                Close
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => testProvider(selectedProvider.value)}
                  disabled={testing === selectedProvider.value}
                  className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1"
                >
                  {testing === selectedProvider.value ? (
                    <>
                      <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test'
                  )}
                </button>
                <button
                  onClick={handleSaveProviderConfig}
                  disabled={saving || !apiKey.trim()}
                  className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1"
                >
                  {saving ? (
                    <>
                      <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Cog6ToothIcon className="h-3.5 w-3.5" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIProviderSettingsPage;
